/**
 * Service du dépôt de photos par lien à usage limité.
 *
 * POURQUOI CE DÉTOUR. Un agent conversationnel ne reçoit pas de fichier : il n'y
 * a aucun chemin par lequel une photo puisse remonter d'une conversation vers un
 * outil. L'agent crée donc un lien, l'utilisateur l'ouvre sur son téléphone,
 * choisit ses photos dans sa galerie, et revient à sa conversation.
 *
 * CES ROUTES SONT SEMI-PUBLIQUES et n'emportent AUCUN en-tête d'authentification.
 * Le secret EST l'authentification : 32 octets aléatoires, une courte durée de
 * vie et un nombre de dépôts borné. Y ajouter un JWT serait non seulement inutile
 * mais nuisible — la page s'ouvre sur un téléphone où l'utilisateur n'est
 * probablement pas connecté, et un en-tête manquant y ferait échouer un appel qui
 * doit réussir.
 *
 * LES MESSAGES DU SERVEUR SONT DÉJÀ RÉDIGÉS POUR UN HUMAIN. Lien expiré, épuisé,
 * inconnu : le serveur sait dire lequel, et il le dit mieux que nous, qui ne
 * distinguerions rien. On relaie donc son `message` tel quel et on ne se rabat
 * sur un texte à nous que lorsqu'il n'en donne aucun.
 */

import { compressImage } from './filesService';
import { enTetesAuth } from './enTetesAuth';

const API_BASE = import.meta.env.VITE_API_BASE_URL;

/**
 * Lit une clé quelle que soit la casse rendue par ColdFusion.
 *
 * queryToArray rabaisse les colonnes en minuscules, mais les structures
 * composées à la main gardent leur casse d'origine, et le canal REST remonte
 * parfois tout en MAJUSCULES. Les trois formes coexistent dans une même
 * réponse : `data` peut arriver en `DATA` pendant que `restant` reste en
 * minuscules. Cette fonction est donc indispensable ici, pas décorative.
 */
const lire = (objet, nom, defaut = '') => {
    if (!objet) return defaut;
    const bas = nom.toLowerCase();
    const haut = nom.toUpperCase();
    if (objet[nom] !== undefined) return objet[nom];
    if (objet[bas] !== undefined) return objet[bas];
    if (objet[haut] !== undefined) return objet[haut];
    return defaut;
};

/** Le corps utile d'une réponse, quelle que soit la casse de ses clés. */
const corps = (json) => ({
    success: lire(json, 'success', false) === true,
    message: lire(json, 'message', ''),
    data: lire(json, 'data', null)
});

/** Un entier, quelle que soit la façon dont ColdFusion l'a sérialisé. */
const entier = (valeur, defaut = 0) => {
    const n = Number(valeur);
    if (Number.isFinite(n)) return n;
    return defaut;
};

/**
 * Ce que la page doit savoir pour s'afficher.
 *
 * NE LÈVE JAMAIS. Un lien mort n'est pas une panne : c'est le cas nominal d'un
 * lien qui a fait son office ou dont le quart d'heure est passé. La page doit
 * pouvoir l'afficher sobrement, sans écran d'erreur. On rend donc toujours une
 * forme exploitable — `{valide, message, ...}` — et le message du serveur est
 * conservé intact.
 *
 * @param {string} secret - le secret du lien, tel qu'il vient de l'URL
 * @returns {Promise<{valide: boolean, message: string, module: string,
 *   label: string, maxFiles: number, usedCount: number, restant: number}>}
 */
export const lireLienDepot = async (secret) => {
    const echec = (message) => ({
        valide: false,
        message,
        module: '',
        label: '',
        maxFiles: 0,
        usedCount: 0,
        restant: 0
    });

    if (!secret) {
        return echec('Ce lien de dépôt est incomplet.');
    }

    let json;
    try {
        const reponse = await fetch(`${API_BASE}/mcp/depot/${encodeURIComponent(secret)}`, {
            method: 'GET',
            headers: { Accept: 'application/json' }
        });
        json = await reponse.json();
    } catch {
        // Réseau coupé ou réponse illisible : ce n'est pas le serveur qui parle,
        // donc aucun message à relayer — c'est le seul cas où le nôtre s'impose.
        return echec('Le lien n\'a pas pu être vérifié. Vérifiez votre connexion et réessayez.');
    }

    const { success, message, data } = corps(json);

    if (!success) {
        return echec(message || 'Ce lien de dépôt n\'est plus valable.');
    }

    const maxFiles = entier(lire(data, 'maxFiles', 0));
    const usedCount = entier(lire(data, 'usedCount', 0));

    return {
        valide: true,
        message,
        module: lire(data, 'module', ''),
        label: lire(data, 'label', ''),
        maxFiles,
        usedCount,
        restant: entier(lire(data, 'restant', maxFiles - usedCount))
    };
};

/**
 * Envoie UN fichier.
 *
 * La route n'en accepte qu'un à la fois : l'appelant boucle et affiche sa
 * progression. C'est aussi ce qui permet qu'un fichier refusé — trop lourd,
 * format inattendu — n'emporte pas les suivants avec lui.
 *
 * On ne pose PAS de `Content-Type` : le navigateur doit le composer lui-même
 * pour y placer la frontière du multipart. L'écrire à la main produit un corps
 * que le serveur ne sait pas découper.
 *
 * NE LÈVE JAMAIS non plus, pour la même raison : la boucle d'envoi doit pouvoir
 * continuer après un refus.
 *
 * @param {string} secret - le secret du lien
 * @param {File} file - le fichier choisi dans la galerie
 * @returns {Promise<{success: boolean, message: string, filePath: string, restant: number|null}>}
 */
export const envoyerFichier = async (secret, file) => {
    if (!secret || !file) {
        return { success: false, message: 'Aucun fichier à envoyer.', filePath: '', restant: null };
    }

    // COMPRESSION AVANT L'ENVOI, ET NON APRÈS.
    //
    // Une photo prise au téléphone pèse trois à cinq mégaoctets. Cette page
    // s'ouvre justement sur un téléphone, souvent en mobile : c'est l'envoi
    // lui-même qui est lent, et compresser côté serveur ne l'éviterait pas.
    //
    // 1600 px et 800 Ko, là où `compressImage` vise 800 px et 50 Ko par défaut.
    // Ces valeurs-là conviennent à une photo de profil affichée en vignette ;
    // une réalisation dont on est fier se regarde en grand, et la réduire
    // autant se verrait. Le compromis retenu divise le poids par cinq environ
    // sans perte visible à l'écran.
    //
    // NE BLOQUE PAS EN CAS D'ÉCHEC : si le navigateur ne sait pas décoder
    // l'image — format exotique, canvas indisponible en navigation privée —
    // on envoie l'original. Une photo lourde vaut mieux qu'une photo perdue.
    let aEnvoyer = file;
    try {
        aEnvoyer = await compressImage(file, 800 * 1024, 1600);
    } catch {
        aEnvoyer = file;
    }

    const formData = new FormData();
    formData.append('file', aEnvoyer);

    let json;
    try {
        const reponse = await fetch(`${API_BASE}/mcp/depot/${encodeURIComponent(secret)}/fichier`, {
            method: 'POST',
            // Multipart : pas de Content-Type, le navigateur pose la frontière.
            headers: enTetesAuth(false),
            body: formData
        });
        json = await reponse.json();
    } catch {
        return {
            success: false,
            message: 'L\'envoi a échoué. Vérifiez votre connexion et réessayez.',
            filePath: '',
            restant: null
        };
    }

    const { success, message, data } = corps(json);

    if (!success) {
        return {
            success: false,
            message: message || 'Cette photo n\'a pas pu être envoyée.',
            filePath: '',
            restant: null
        };
    }

    // `restant` absent n'est pas zéro : c'est « le serveur ne l'a pas dit ».
    // On rend null pour que l'appelant décrémente son propre compteur plutôt
    // que d'afficher un quota épuisé qui ne l'est pas.
    const restantBrut = lire(data, 'restant', null);
    let restant = null;
    if (restantBrut !== null && restantBrut !== '' && Number.isFinite(Number(restantBrut))) {
        restant = Number(restantBrut);
    }

    return {
        success: true,
        message,
        filePath: lire(data, 'filePath', ''),
        restant
    };
};
